import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import { type BaseCustomField } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentBoardPermissions } from '~/hooks/useCurrentBoardPermissions';
import { useFetchAnyWorkspacePermissions } from '~/hooks/useFetchAnyWorkspacePermissions';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useGetPrivateCustomFields } from '~/swr-hooks/customFields/useGetPrivateCustomFields';
import { isDevOrTestStage } from '~/swr-hooks/utils';
import { canSeeCustomFields } from '~/utils/permissions/workspacePermissions';

type PrivateCustomFieldsProviderProps = PropsWithChildren<object>;

export type PrivateCustomFieldsProviderContextValue = {
  customFields: BaseCustomField[] | undefined;
  isReady: boolean;
  error?: unknown;
};

const defaultValue: PrivateCustomFieldsProviderContextValue = {
  customFields: undefined,
  isReady: false,
};

const PrivateCustomFieldsProviderContext = createContext<PrivateCustomFieldsProviderContextValue>(defaultValue);

export const PrivateCustomFieldsProvider = ({ children }: PrivateCustomFieldsProviderProps) => {
  const { data: workspacePermissions } = useFetchAnyWorkspacePermissions();
  const { boardPermissions } = useCurrentBoardPermissions();
  const boardId = useURLBoardIdSelector();
  const permissionsKnown = boardId ? boardPermissions !== undefined : workspacePermissions !== undefined;
  const canViewCustomFields = canSeeCustomFields(boardId ? boardPermissions : workspacePermissions);
  const { data: customFields, error, isFetched } = useGetPrivateCustomFields(canViewCustomFields);
  const isReady = permissionsKnown && (!canViewCustomFields || isFetched);

  const value = useMemo(
    () => ({ customFields: customFields?.data, error, isReady }),
    [customFields?.data, error, isReady],
  );

  return (
    <PrivateCustomFieldsProviderContext.Provider value={value}>{children}</PrivateCustomFieldsProviderContext.Provider>
  );
};

export const usePrivateCustomFieldsContext = () => {
  const context = useContext(PrivateCustomFieldsProviderContext);

  if (context === defaultValue) {
    const error = 'PrivateCustomFieldsProviderContext used outside of PrivateCustomFieldsProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
